// =============================================================================
// screens/PortfolioScreen.tsx — "Your Portfolio" (route: Portfolio; IMG_5874 +
// IMG_5847 fix). The destination for the home portfolio card, turning the dead
// "$0.00" slab into a v2 FOMO moment:
//
//   - Coins | NFTs tabs (IMG_5874 structure, lavender glass palette)
//   - beautiful empty/preview states for both tabs (dimmed preview cards with
//     Coming-Soon badges — nothing pretends to be real holdings)
//   - the Cryb Wallet onboarding sheet (IMG_5881) as the first-run path —
//     rendered but GATED: "Create My Wallet" / "I already have a wallet" both
//     resolve to Coming Soon ("Wallets land in CRYB v2").
//
// v1 crypto policy: this surface is visible, gorgeous and fully blocked. No
// wallet/balance/transaction endpoint is called.
//
// Apple-aligned treatment (matches Home/Search/Notifications/DM/Communities +
// CoinDetail): full-bleed <ParticleBackground/> aurora under the status bar, a
// self-managed translucent GlassSurface header (native bar hidden, skill §12),
// content scrolling underneath. The screen stays fully v2-gated — only the
// visual/UX layer changed.
//
// REGISTRATION (orchestrator, App.tsx root stack):
//   <RootStack.Screen name="Portfolio" component={PortfolioScreen}
//     options={{ ...headerStyle, title: 'Your Portfolio' }} />
// and point the home PortfolioCard's onPress here (app/(tabs)/home.tsx).
// =============================================================================
import React, { useLayoutEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons, IoniconName } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';
import { GlassCard, GlassButton, GlassPill, GlassPillGroup, GlassSurface } from '../components/glass';
import ComingSoonBadge from '../components/ComingSoonBadge';
import WalletOnboardingSheet from '../components/markets/WalletOnboardingSheet';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  shadowTokens,
  colorTokens,
  layout as layoutTokens,
  getLayout,
} from '../constants/theme';
import { glassTokens } from '../components/glass';
import { tabularNums } from '../lib/format';

type Tab = 'Coins' | 'NFTs';

// Palette-derived tints + preview art ramps (no ad-hoc rgba/hex inline). The NFT
// preview gradients stay in the brand lavender family — dimmed, illustrative
// chrome, never fake holdings.
const TINT = {
  primarySoft: 'rgba(108,92,231,0.12)',
  primaryFaint: 'rgba(108,92,231,0.10)',
  primaryBar: 'rgba(108,92,231,0.18)',
  onGlass: 'rgba(255,255,255,0.85)',
} as const;
const NFT_ART: [string, string][] = [
  ['#B7A6F0', '#8E7BE8'],
  ['#D3C6F5', '#A896EC'],
];

// A dimmed, clearly-labeled preview row/card. Content is illustrative UI
// chrome, not fake balances — the value slots render "··· ".
function PreviewCoinRow({ icon, name, symbol }: { icon: IoniconName; name: string; symbol: string }) {
  return (
    <View style={styles.previewRow}>
      <View style={styles.previewCoinIcon}>
        <Ionicons name={icon} size={16} color={colors.primary} />
      </View>
      <View style={{ flex: 1 }}>
        <Text style={styles.previewCoinName}>{name}</Text>
        <Text style={styles.previewCoinSymbol}>{symbol}</Text>
      </View>
      <View style={styles.previewValueBlock}>
        <View style={styles.previewValueBar} />
        <View style={[styles.previewValueBar, styles.previewValueBarShort]} />
      </View>
    </View>
  );
}

export default function PortfolioScreen() {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  // Center + width-cap the content on iPad/desktop; phone stays edge-to-edge.
  const containerStyle =
    layout !== 'phone'
      ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
      : undefined;
  const [tab, setTab] = useState<Tab>('Coins');
  const [sheetOpen, setSheetOpen] = useState(false);

  // Self-manage the header (skill §12): hide the opaque native bar so the aurora
  // shows through the status bar and content scrolls under an in-screen glass
  // header. Runs before paint to avoid a flash of the native bar.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  return (
    <View style={styles.screen}>
      {/* Full-bleed aurora under the status bar + translucent header (skill §12). */}
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />

      {/* In-screen translucent header: back chevron + title. Content scrolls
          underneath, so there is no opaque strip / gap at the top. */}
      <GlassSurface
        variant="bar"
        mode="light"
        radius={0}
        contentStyle={[styles.headerContent, { paddingTop: insets.top }]}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Ionicons name="chevron-back" size={24} color={colors.ink} />
        </TouchableOpacity>
        <Text style={styles.headerTitle} numberOfLines={1}>Your Portfolio</Text>
        <View style={styles.headerRightSpacer} />
      </GlassSurface>

      <ScrollView
        style={styles.flex}
        contentContainerStyle={[styles.content, containerStyle]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Hero: portfolio value, honest and beautiful ── */}
        <GlassCard style={styles.hero} contentStyle={styles.heroContent}>
          <LinearGradient
            colors={colors.gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.heroGlyph}
          >
            <Ionicons name="wallet-outline" size={22} color={colors.white} />
          </LinearGradient>
          <Text style={styles.heroLabel}>Portfolio Value</Text>
          <Text style={styles.heroValue}>$0.00</Text>
          <ComingSoonBadge label="Wallets · CRYB v2" />
          <Text style={styles.heroSub}>
            Your coins and NFTs will live here the moment CRYB Wallet lands.
          </Text>
        </GlassCard>

        {/* ── Coins | NFTs tabs (IMG_5874) ── */}
        <GlassPillGroup style={styles.tabRow}>
          <GlassPill label="Coins" icon="logo-bitcoin" active={tab === 'Coins'} onPress={() => setTab('Coins')} />
          <GlassPill label="NFTs" icon="images-outline" active={tab === 'NFTs'} onPress={() => setTab('NFTs')} />
        </GlassPillGroup>

        {tab === 'Coins' ? (
          <GlassCard style={styles.panel} contentStyle={styles.panelContent}>
            <Text style={styles.panelTitle}>No coins yet</Text>
            <Text style={styles.panelSub}>
              Create your CRYB Wallet to hold, track and flex your coins — all
              without leaving the app.
            </Text>
            {/* Preview of what the tab becomes (dimmed, no fake numbers). */}
            <View style={styles.previewStack}>
              <PreviewCoinRow icon="logo-bitcoin" name="Bitcoin" symbol="BTC" />
              <PreviewCoinRow icon="diamond-outline" name="Ethereum" symbol="ETH" />
              <PreviewCoinRow icon="flash-outline" name="Solana" symbol="SOL" />
            </View>
            <GlassButton
              title="Create My Wallet"
              icon="sparkles-outline"
              variant="primary"
              size="lg"
              fullWidth
              onPress={() => setSheetOpen(true)}
            />
          </GlassCard>
        ) : (
          <GlassCard style={styles.panel} contentStyle={styles.panelContent}>
            <Text style={styles.panelTitle}>Your gallery awaits</Text>
            <Text style={styles.panelSub}>
              Big artwork, floor prices and one-tap showing off — your NFT
              collection gets a proper home in CRYB v2.
            </Text>
            {/* NFT card previews (IMG_5874: large art + name + standard). */}
            <View style={styles.nftPreviewRow}>
              {[0, 1].map((i) => (
                <View key={i} style={styles.nftCard}>
                  <LinearGradient
                    colors={NFT_ART[i] ?? NFT_ART[0]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.nftArt}
                  >
                    <Ionicons name="image-outline" size={26} color={TINT.onGlass} />
                  </LinearGradient>
                  <View style={styles.nftTitleBar} />
                  <View style={[styles.nftTitleBar, styles.nftTitleBarShort]} />
                </View>
              ))}
            </View>
            <GlassButton
              title="Create My Wallet"
              icon="sparkles-outline"
              variant="primary"
              size="lg"
              fullWidth
              onPress={() => setSheetOpen(true)}
            />
          </GlassCard>
        )}

        {/* Secondary path (also gated inside the sheet). */}
        <Text style={styles.altLink} onPress={() => setSheetOpen(true)}>
          I already have a wallet
        </Text>
      </ScrollView>

      <WalletOnboardingSheet visible={sheetOpen} onClose={() => setSheetOpen(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  flex: { flex: 1 },
  content: {
    // Clears the floating translucent header (safe-area added inline above) and
    // leaves a comfortable bottom gutter below the last CTA.
    paddingTop: spacing.md,
    paddingBottom: spacing.xxl,
  },

  // --- In-screen translucent header (skill §12) ---
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm + 2,
    minHeight: layoutTokens.headerHeight,
  },
  headerTitle: {
    ...typographyScale.h3,
    ...fonts.semibold,
    color: colors.ink,
    flex: 1,
    letterSpacing: -0.3, // tighten the title (skill §15)
  },
  headerRightSpacer: { width: 24 },

  hero: { marginHorizontal: spacing.lg, ...shadowTokens.card },
  heroContent: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.xxl,
  },
  heroGlyph: {
    width: 52,
    height: 52,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadowTokens.bar,
    shadowColor: colors.primary,
  },
  heroLabel: { ...typographyScale.caption, ...fonts.medium, fontSize: 13, color: colorTokens.light.textFaint },
  heroValue: { ...typographyScale.h1, ...fonts.extrabold, fontSize: 40, lineHeight: 44, color: colorTokens.light.text, letterSpacing: -1, ...tabularNums },
  heroSub: {
    ...typographyScale.body,
    fontSize: 13,
    lineHeight: 19,
    color: colorTokens.light.textMuted,
    textAlign: 'center',
    paddingHorizontal: spacing.md,
  },
  tabRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  panel: { marginHorizontal: spacing.lg, ...shadowTokens.card },
  panelContent: { gap: spacing.md },
  panelTitle: { ...typographyScale.h3, ...fonts.bold, color: colorTokens.light.text },
  panelSub: { ...typographyScale.body, fontSize: 13, lineHeight: 20, color: colorTokens.light.textMuted },
  previewStack: {
    gap: spacing.sm,
    opacity: 0.55, // preview, not real holdings
  },
  previewRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: glassTokens.surface.card,
    borderRadius: radius.md,
    padding: spacing.md,
    borderWidth: 1,
    borderColor: glassTokens.border,
  },
  previewCoinIcon: {
    width: 32,
    height: 32,
    borderRadius: radius.full,
    backgroundColor: TINT.primarySoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewCoinName: { ...fonts.semibold, fontSize: 13, color: colorTokens.light.text },
  previewCoinSymbol: { ...fonts.medium, fontSize: 11, color: colorTokens.light.textFaint },
  previewValueBlock: { alignItems: 'flex-end', gap: 5 },
  previewValueBar: {
    width: 54,
    height: 8,
    borderRadius: 4,
    backgroundColor: TINT.primaryBar,
  },
  previewValueBarShort: { width: 34, backgroundColor: TINT.primaryFaint },
  nftPreviewRow: {
    flexDirection: 'row',
    gap: spacing.md,
    opacity: 0.75,
  },
  nftCard: { flex: 1, gap: 6 },
  nftArt: {
    aspectRatio: 1,
    borderRadius: radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nftTitleBar: {
    width: '70%',
    height: 9,
    borderRadius: 5,
    backgroundColor: TINT.primaryBar,
  },
  nftTitleBarShort: { width: '40%', backgroundColor: TINT.primaryFaint },
  altLink: {
    ...fonts.semibold,
    fontSize: 14,
    color: colors.primary,
    textAlign: 'center',
    marginTop: spacing.lg,
    paddingVertical: spacing.sm,
  },
});
